import type { RenderTriggerArgs, ListProps } from '@mfw/components';

import {
  useTranslationsProvider,
  Autocomplete,
  Iconify,
  useIntl,
} from '@mfw/components';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { FormAutocomplete } from '@mfw/forms';
import classNames from 'classnames';
import React from 'react';

import {
  type AppLocale,
  LOCALES,
  resolveAppLocale,
} from '../../config/locales';
import { useUpdateUser } from '../../hooks/usersv2';
import { useAuthContext } from '../../context';
import messages from './messages';
import './LocaleSelector.scss';

export type FormFieldLocaleSelectorProps = {
  onChange?: (locale: AppLocale) => void;
  'data-testid'?: string;
  asFormField: boolean;
  readOnly?: boolean;
  label: string;
  name: string;
};
export type LocaleSelectorProps =
  | FormFieldLocaleSelectorProps
  | Pick<FormFieldLocaleSelectorProps, 'onChange'>;
const LocaleSelector = (
  props: { readOnly?: boolean } & LocaleSelectorProps,
) => {
  const { formatMessage } = useIntl();
  const { currentUser } = useAuthContext();
  const { mutateAsync: updateUserSettingsAsync } = useUpdateUser();
  const { locale, updateLocaleAsync } = useTranslationsProvider();
  // Always an entry, never `undefined` — the trigger below renders its flag
  // into `Iconify`'s required `icon` prop, and an account carrying a locale the
  // app no longer supports used to blank the button out.
  const [selectedLocale, setSelectedLocale] = React.useState<AppLocale>(() =>
    resolveAppLocale(locale),
  );

  const handleChange = React.useCallback(
    async (availableLocale: AppLocale) => {
      // A visitor has no settings to write to: the route answers 403
      if (!currentUser) {
        await updateLocaleAsync(availableLocale.value);
        return;
      }
      await updateUserSettingsAsync({
        userSettings: { locale: [{ value: availableLocale.value }] },
      });
      setSelectedLocale(availableLocale);
    },
    [currentUser, updateLocaleAsync, updateUserSettingsAsync],
  );

  const ownLocale = currentUser?.settings?.ownLocale;
  React.useEffect(() => {
    if (ownLocale) {
      updateLocaleAsync(ownLocale as any);
    }
  }, [updateLocaleAsync, ownLocale]);

  // The flag follows the app's language, whoever set it: the tenant's arrives
  // after this mounts.
  React.useEffect(() => {
    setSelectedLocale(resolveAppLocale(locale));
  }, [locale]);

  const options: ListProps['items'] = React.useMemo(() => {
    return LOCALES.map((locale) => ({
      content: ({ isSelected, onSelected, option }) => (
        <div
          className={classNames('Mfw-LocaleSelector__item-content', {
            'Mfw-LocaleSelector__item-content--selected': isSelected,
          })}
          onClick={() => onSelected?.(option)}
        >
          <Iconify icon={locale.flag} size="medium" />
          <Typography
            className="Mfw-LocaleSelector__item-label"
            component="span"
          >
            {locale.label}
          </Typography>
        </div>
      ),
      label: locale.label,
      value: locale.value,
    }));
  }, []);

  const renderTrigger = React.useCallback(
    ({ getReferenceProps, refs }: RenderTriggerArgs) => {
      return (
        <IconButton
          ref={(e) => {
            refs.setReference(e);
          }}
          data-testid="locale-selector-trigger"
          {...getReferenceProps()}
        >
          <Iconify
            className="shadow-2xl rounded-lg"
            icon={selectedLocale.flag}
            size="large"
          />
        </IconButton>
      );
    },
    [selectedLocale.flag],
  );

  const handleSelected = React.useCallback(
    (options: ListProps['items']) => {
      const selected = options?.[0];

      const found = LOCALES.find((locale) => locale.value === selected?.value);
      if (found && !('asFormField' in props && props.asFormField)) {
        handleChange(found);
      }
    },
    [handleChange, props],
  );

  const nonFormFieldDefaultValue = React.useMemo(() => {
    return currentUser?.settings?.ownLocale
      ? [{ value: currentUser?.settings?.ownLocale }]
      : undefined;
  }, [currentUser?.settings?.ownLocale]);

  const args = React.useMemo(
    () => ({
      classes: {
        dropdownMenu: {
          root: 'Mfw-LocaleSelector-dropdownMenu__root',
        },
      },
      onSelected: handleSelected,

      options,
      title: formatMessage(messages.selectLanguage),
      total: options.length,
      withoutDeselect: true,
      withoutSearch: true,
    }),
    [formatMessage, handleSelected, options],
  );

  if ('asFormField' in props && props.asFormField) {
    return (
      <FormAutocomplete
        readOnly={props.readOnly}
        {...args}
        data-testid={props['data-testid']}
        title={props.label || ''}
        label={props.label}
        name={props.name}
      />
    );
  }

  return (
    <Autocomplete
      readOnly={props.readOnly}
      {...args}
      defaultValue={nonFormFieldDefaultValue}
      renderTrigger={renderTrigger}
    />
  );
};

export default LocaleSelector;
